import { InfiniteData, QueryClient } from '@tanstack/react-query';
import { IFieldDetails, IPropertyField } from '../types/api';
import { getQueryClient } from '@/modules/shared/queryClientSingleton';

export const PLOTS_CACHE_STORAGE_KEY = 'orth-app-plots-cache';

type PlotsQuery = InfiniteData<{ fields: IPropertyField[]; total: number }>;

const UNFILTERED_PLOTS_QUERY_KEY = ['PLOTS', undefined, undefined, undefined] as const;

function readUnfilteredPlotsCache(): PlotsQuery | undefined {
  return getQueryClient()?.getQueryData<PlotsQuery>(UNFILTERED_PLOTS_QUERY_KEY);
}

/** Unique plots from the PLOTS cache (deduped by id). */
export function getExistingPlotsFromCache(): IPropertyField[] {
  const fields = readUnfilteredPlotsCache()?.pages.flatMap((p) => p.fields) ?? [];
  const seen = new Set<number>();
  return fields.filter((f) => {
    if (seen.has(f.id)) return false;
    seen.add(f.id);
    return true;
  });
}

/**
 * Unique plot count for auto-naming (`Plot_N` → N = this count + 1).
 * Uses server `total` when present so naming stays correct before all pages load.
 */
export function getUniquePlotCountFromCache(): number {
  const data = readUnfilteredPlotsCache();
  const uniquePlots = getExistingPlotsFromCache();
  const serverTotal = data?.pages[0]?.total;
  if (typeof serverTotal === 'number' && serverTotal > 0) {
    return Math.max(serverTotal, uniquePlots.length);
  }
  return uniquePlots.length;
}

type PlotsPage = { fields: IPropertyField[]; total: number };
type PlotsInfiniteData = InfiniteData<PlotsPage>;

export function patchFieldDetailsCache(
  queryClient: QueryClient,
  fieldId: number | undefined,
  updater: (old: IFieldDetails) => IFieldDetails
) {
  if (fieldId == null) return;
  queryClient.setQueryData<IFieldDetails>(['FIELD_DETAILS', fieldId], (old) =>
    old ? updater(old) : old
  );
}

export function clearPlotsCache() {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(PLOTS_CACHE_STORAGE_KEY);
  }
}

export function appendPlotsToCache(queryClient: QueryClient, newPlots: IPropertyField[]) {
  if (newPlots.length === 0) return;
  queryClient.setQueriesData<PlotsInfiniteData>({ queryKey: ['PLOTS'] }, (old) => {
    if (!old) return old;
    const [first, ...rest] = old.pages;
    const existing = first?.fields ?? [];
    const existingIds = new Set(existing.map((f) => f.id));
    const toAdd = newPlots.filter((p) => !existingIds.has(p.id));
    if (toAdd.length === 0) return old;
    const merged = [...existing, ...toAdd];
    return {
      ...old,
      pages: [
        {
          ...first,
          fields: merged,
          total: Math.max(first?.total ?? 0, merged.length),
        },
        ...rest,
      ],
    };
  });
}

export function updatePlotsInCache(
  queryClient: QueryClient,
  updates: (Omit<Partial<IFieldDetails>, 'id'> & { id: number })[]
) {
  if (updates.length === 0) return;
  queryClient.setQueriesData<PlotsInfiniteData>(
    { queryKey: ['PLOTS'] },
    (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          fields: page.fields.map((f) => {
            const update = updates.find((u) => u.id === f.id);
            if (!update) return f;
            return {
              id: update.id,
              name: update.plotName || f.name,
              area: update.area || f.area,
              areaUnit: update.areaUnit || f.areaUnit,
              boundary: update.boundary || f.boundary,
              crop: update.crop || f.crop,
              completionRate: update.completionRate || f.completionRate,
              actionStatus: update.actionStatus || f.actionStatus,
              attachmentSummary:
                update.attachmentSummary || f.attachmentSummary,
            } satisfies IPropertyField;
          }),
        })),
      };
    }
  );
}

export function removePlotsFromCache(queryClient: QueryClient, plotIds: number[]) {
  const idSet = new Set(plotIds);
  queryClient.setQueriesData<PlotsInfiniteData>({ queryKey: ['PLOTS'] }, (old) => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((page, index) => ({
        ...page,
        fields: page.fields.filter((f) => !idSet.has(f.id)),
        total: index === 0 ? Math.max(0, (page.total ?? 0) - plotIds.length) : page.total,
      })),
    };
  });
}

export function removePlotFromCache(queryClient: QueryClient, plotId: number) {
  queryClient.setQueriesData<PlotsInfiniteData>({ queryKey: ['PLOTS'] }, (old) => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((page, index) => ({
        ...page,
        fields: page.fields.filter((f) => f.id !== plotId),
        total: index === 0 ? Math.max(0, (page.total ?? 0) - 1) : page.total,
      })),
    };
  });
}
